import React from 'react';

const HowToUse = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow-lg rounded-lg p-8 mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          How to Use the NIS2 Risk Assessment Tool
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          Follow these step-by-step instructions to determine your organization's appropriate CyberFundamentals assurance level
        </p>

        <div className="prose prose-lg max-w-none">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Step-by-Step Guide</h2>
          
          <div className="space-y-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">1</div>
                <h3 className="text-xl font-semibold text-blue-800">Select Your Sector</h3>
              </div>
              <p className="text-blue-700 mb-4">
                Choose the sector that best represents your organization's primary business activities from the dropdown menu.
              </p>
              <div className="bg-blue-100 p-4 rounded-lg">
                <p className="text-blue-800 font-medium">Available Sectors:</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-blue-700">
                  <div>• Energy</div>
                  <div>• Transport</div>
                  <div>• Finance</div>
                  <div>• Healthcare</div>
                  <div>• Water</div>
                  <div>• ICT</div>
                  <div>• Food</div>
                  <div>• Manufacturing</div>
                  <div>• Chemicals</div>
                  <div>• Aerospace</div>
                  <div>• Research</div>
                  <div>• Postal</div>
                  <div>• Waste</div>
                  <div>• Space</div>
                  <div>• Public Administration</div>
                  <div>• Social Media</div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">2</div>
                <h3 className="text-xl font-semibold text-green-800">Set Organization Size</h3>
              </div>
              <p className="text-green-700 mb-4">
                Select your organization size, which affects the risk calculation multiplier:
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-100 p-3 rounded-lg text-center">
                  <div className="font-semibold text-green-800">Small</div>
                  <div className="text-sm text-green-700">Multiplier: 1x</div>
                </div>
                <div className="bg-green-100 p-3 rounded-lg text-center">
                  <div className="font-semibold text-green-800">Medium</div>
                  <div className="text-sm text-green-700">Multiplier: 2x</div>
                </div>
                <div className="bg-green-100 p-3 rounded-lg text-center">
                  <div className="font-semibold text-green-800">Large</div>
                  <div className="text-sm text-green-700">Multiplier: 3x</div>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">3</div>
                <h3 className="text-xl font-semibold text-orange-800">Configure Impact Levels</h3>
              </div>
              <p className="text-orange-700 mb-4">
                Set the impact level for each cyber attack category based on your organization's specific risk profile:
              </p>
              <div className="space-y-3">
                <div className="bg-orange-100 p-4 rounded-lg">
                  <div className="font-semibold text-orange-800 mb-2">Impact Level Options:</div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Low (0):</span> Minimal impact on operations
                    </div>
                    <div>
                      <span className="font-medium">Medium (5):</span> Moderate impact, some disruption
                    </div>
                    <div>
                      <span className="font-medium">High (10):</span> Severe impact, significant disruption
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">4</div>
                <h3 className="text-xl font-semibold text-purple-800">Set Threat Actor Probabilities</h3>
              </div>
              <p className="text-purple-700 mb-4">
                For each attack category, set the probability that different threat actors might target your organization:
              </p>
              <div className="space-y-4">
                <div className="bg-purple-100 p-4 rounded-lg">
                  <div className="font-semibold text-purple-800 mb-2">Attack Categories:</div>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• <strong>Sabotage/Disruption:</strong> DDoS attacks, system disruption</li>
                    <li>• <strong>Information Theft:</strong> Espionage, data breaches</li>
                    <li>• <strong>Crime:</strong> Ransomware, financial fraud</li>
                    <li>• <strong>Hacktivism:</strong> Subversion, website defacement</li>
                    <li>• <strong>Disinformation:</strong> Political influence, fake news</li>
                  </ul>
                </div>
                <div className="bg-purple-100 p-4 rounded-lg">
                  <div className="font-semibold text-purple-800 mb-2">Threat Actors:</div>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• <strong>Competitors:</strong> Business rivals, industrial espionage</li>
                    <li>• <strong>Ideologues:</strong> Political or social activists</li>
                    <li>• <strong>Terrorists:</strong> Organized terrorist groups</li>
                    <li>• <strong>Cyber Criminals:</strong> Financially motivated attackers</li>
                    <li>• <strong>Nation State Actors:</strong> State-sponsored attacks</li>
                  </ul>
                </div>
                <div className="bg-purple-100 p-4 rounded-lg">
                  <div className="font-semibold text-purple-800 mb-2">Probability Levels:</div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Low (0):</span> Unlikely to be targeted
                    </div>
                    <div>
                      <span className="font-medium">Medium (0.5):</span> Possible target
                    </div>
                    <div>
                      <span className="font-medium">High (1):</span> Likely target
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">5</div>
                <h3 className="text-xl font-semibold text-red-800">Review Risk Assessment Results</h3>
              </div>
              <p className="text-red-700 mb-4">
                The tool automatically calculates your risk scores and determines the appropriate CyFun assurance level:
              </p>
              <div className="space-y-3">
                <div className="bg-red-100 p-4 rounded-lg">
                  <div className="font-semibold text-red-800 mb-2">Risk Calculation Formula:</div>
                  <div className="text-sm text-red-700 font-mono bg-white p-2 rounded">
                    Risk Score = Probability × Impact × Attack Type × Organization Size
                  </div>
                </div>
                <div className="bg-red-100 p-4 rounded-lg">
                  <div className="font-semibold text-red-800 mb-2">CyFun Level Determination:</div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-medium text-green-700">BASIC</div>
                      <div>Score: 0-99</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-orange-700">IMPORTANT</div>
                      <div>Score: 100-199</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-red-700">ESSENTIAL</div>
                      <div>Score: 200+</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-8">
            <h3 className="text-lg font-semibold text-yellow-800 mb-3">💡 Best Practices</h3>
            <ul className="text-yellow-700 space-y-2">
              <li>• <strong>Document your decisions:</strong> Keep records of why you selected specific probability and impact levels</li>
              <li>• <strong>Regular reviews:</strong> Reassess your risk profile periodically as your organization evolves</li>
              <li>• <strong>Multi-sector organizations:</strong> If you operate in multiple sectors, use the highest assurance level determined</li>
              <li>• <strong>Expert consultation:</strong> Consider consulting with cybersecurity professionals for complex risk assessments</li>
              <li>• <strong>Compliance focus:</strong> Ensure your assessment aligns with NIS2 directive requirements</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">📞 Need Help?</h3>
            <p className="text-blue-700">
              For additional guidance or feedback on the CyFun framework, you can contact the Centre for Cybersecurity Belgium at:
              <br />
              <span className="font-mono bg-blue-100 px-2 py-1 rounded">certification@ccb.belgium.be</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToUse;